import { Injectable } from '@angular/core';
import { toast } from 'ngx-sonner';
import { HttpErrorResponse } from '@angular/common/http';

export interface ToastConfig {
  title: string;
  description?: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly defaultDuration = 1000;

  /**
   * Show a success toast with standard Italian messaging
   */
  public showSuccess(config?: Partial<ToastConfig>): void {
    const finalConfig: ToastConfig = {
      title: 'Fatto!',
      description: 'Operazione completata con successo',
      duration: this.defaultDuration,
      ...config,
    };

    toast(finalConfig.title, {
      description: finalConfig.description,
      duration: finalConfig.duration,
    });
  }

  /**
   * Show an error toast with automatic Italian error mapping
   */
  public showError(error?: HttpErrorResponse | Error | string, config?: Partial<ToastConfig>): void {
    const errorMessage = this.getErrorMessage(error);

    const finalConfig: ToastConfig = {
      title: 'Qualcosa è andato storto',
      description: errorMessage,
      duration: this.defaultDuration,
      ...config,
    };

    toast(finalConfig.title, {
      description: finalConfig.description,
      duration: finalConfig.duration,
    });
  }

  /**
   * Show an info toast with standard Italian messaging
   */
  public showInfo(config?: Partial<ToastConfig>): void {
    const finalConfig: ToastConfig = {
      title: 'Info',
      description: '',
      duration: this.defaultDuration,
      ...config,
    };

    toast(finalConfig.title, {
      description: finalConfig.description,
      duration: finalConfig.duration,
    });
  }

  /**
   * Show a custom toast
   */
  public showToast(config: ToastConfig): void {
    toast(config.title, {
      description: config.description,
      duration: config.duration || this.defaultDuration,
    });
  }

  /**
   * Map HTTP errors and other errors to Italian messages
   */
  private getErrorMessage(error?: HttpErrorResponse | Error | string): string {
    if (!error) {
      return 'Si è verificato un errore imprevisto';
    }

    // String error
    if (typeof error === 'string') {
      return error;
    }

    // HTTP Error Response
    if (error instanceof HttpErrorResponse) {
      return this.getHttpErrorMessage(error);
    }

    // Generic Error
    if (error instanceof Error) {
      return error.message || 'Errore imprevisto';
    }

    return 'Si è verificato un errore imprevisto';
  }

  /**
   * Map HTTP status codes to Italian messages
   */
  private getHttpErrorMessage(error: HttpErrorResponse): string {
    const url = error.url || '';
    const method = this.getHttpMethod(url);
    const operation = this.getOperationType(method, url);

    switch (error.status) {
      case 0:
      case 504:
        return 'Connessione non disponibile. Riprova tra un momento';

      case 400:
        return `Dati non validi per ${operation}`;

      case 401:
        return 'Accesso non autorizzato. Effettua nuovamente il login';

      case 403:
        return 'Non hai i permessi per questa operazione';

      case 404:
        return `${operation} non trovato/a`;

      case 409:
        return 'Conflitto: l\'operazione non può essere completata';

      case 422:
        return 'Dati forniti non validi';

      case 429:
        return 'Troppe richieste. Riprova tra un momento';

      case 500:
      case 502:
      case 503:
        return 'Errore del server. Riprova tra qualche minuto';

      default:
        return `Errore durante ${operation}. Riprova`;
    }
  }

  /**
   * Extract HTTP method from request (simplified heuristic)
   */
  private getHttpMethod(url: string): string {
    // This is a simplified approach - in a real interceptor we'll have access to the actual method
    if (url.includes('/delete') || url.includes('/remove')) return 'DELETE';
    if (url.includes('/update') || url.includes('/edit')) return 'PUT';
    if (url.includes('/create') || url.includes('/add')) return 'POST';
    return 'GET';
  }

  /**
   * Map URL and method to Italian operation description
   */
  private getOperationType(method: string, url: string): string {
    const isVisit = url.includes('visit') || url.includes('consultation');
    let isTest = (url.includes('test') || url.includes('prescription')) && (!url.includes("s3"));
    const isDoctor = url.includes('doctor');
    const isPatient = url.includes('patient');
    const isClinic = url.includes('clinic');
    const isUser = url.includes('user');
    const isDocument = url.includes('document');

    let entity = 'l\'operazione';
    if (isVisit) entity = 'la visita';
    else if (isDocument) entity = 'il documento';
    else if (isTest) entity = 'la prescrizione';
    else if (isDoctor) entity = 'il medico';
    else if (isPatient) entity = 'il paziente';
    else if (isClinic) entity = 'la clinica';
    else if (isUser) entity = 'l\'utente';

    switch (method) {
      case 'POST':
        return `la creazione di ${entity}`;
      case 'PUT':
      case 'PATCH':
        return `l\'aggiornamento di ${entity}`;
      case 'DELETE':
        return `l\'eliminazione di ${entity}`;
      case 'GET':
      default:
        return `il caricamento di ${entity}`;
    }
  }

  /**
   * Show success toast for HTTP operations
   */
  public showHttpSuccess(method: string, url: string, config?: Partial<ToastConfig>): void {
    const operation = this.getOperationType(method, url);
    const message = this.getSuccessMessage(method, operation);

    this.showSuccess({
      description: message,
      ...config,
    });
  }

  /**
   * Map HTTP operations to Italian success messages
   */
  private getSuccessMessage(method: string, operation: string): string {
    switch (method) {
      case 'POST':
        return operation.replace('la creazione di', 'Creazione completata per');
      case 'PUT':
      case 'PATCH':
        return operation.replace('l\'aggiornamento di', 'Aggiornamento completato per');
      case 'DELETE':
        return operation.replace('l\'eliminazione di', 'Eliminazione completata per');
      default:
        return 'Operazione completata con successo';
    }
  }
}